import { PAListStore } from "@/Appointments/Stores/PAListStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class PAListSettingsContainerHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();
        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Calendar.PAList_SearchSettings_ClinicianFilterTextChanged, this.ClinicianFilterChanged);
        this.RegisterOperation(LocalOperations.Calendar.PAList_Setting_Click, this.DisplayCalendarSettings);
        this.RegisterOperation(LocalOperations.Calendar.PAList_OnSettingsContainerCheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Calendar.PAList_SaveSettingsSelected, this.ToggleSaveSettings);
        this.RegisterOperation(LocalOperations.Calendar.PAList_Setting_CancelClick, this.OnCancelClick);
        this.RegisterOperation(LocalOperations.Calendar.PAList_Setting_OkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Calendar.PAList_ColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Calendar.PAList_ColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.OnContainerClick, false);
    }

    private ToggleSaveSettings(request: LocalEvent, store: PAListStore): void {
        let checked = store.PAListModel.get("SaveSettingsChecked");
        store.PAListModel = store.PAListModel.set("SaveSettingsChecked", !checked);
    }

    private ClinicianFilterChanged(request: LocalEvent, store: PAListStore): void {
        store.PAListModel = store.PAListModel.set("CliniciansSearchText", request.Get<string>("value"));
        store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesFiltered", store.getCliniciansBasedOnSitesAndQuery());
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: PAListStore): void {
        store.PAListModel = store.PAListModel.set("ColumnOrderingPopupOpen", true);
        store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: PAListStore): void {
        store.PAListModel = store.PAListModel.set("ColumnOrderingPopupOpen", false);
        store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", false);
        sessionStore.hideGlobalOverlay();
    }

    private DisplayCalendarSettings(request: LocalEvent, store: PAListStore): void {
        let open = store.PAListModel.get("ShowPAListDisplaySettings");
        store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", !open);
    }

    private OnContainerClick(request: LocalEvent, store: PAListStore): void {
        if (store.PAListModel.get("ShowPAListDisplaySettings") === true) {
            store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", false);
            sessionStore.hideGlobalOverlay();
            this.OnOkClick(request, store);
        }
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: PAListStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_PAList_Sites:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("Sites", store.Helper.FindUpdateICheckableItem<SiteUIModel>(store.PADisplaySettingsModel.get("Sites"), id, checked));
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesFiltered", store.getCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_PAList_Clinicians:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.PADisplaySettingsModel.get("EmployeesGlobalCache"), id, checked));
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesFiltered", store.getCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_PAList_SitesControlCheck:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("IsAllSitesChecked", checked);
                var sites = store.Helper.ChangeCheckStateFromAllICheckable(store.PADisplaySettingsModel.get("Sites"), checked);
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("Sites", sites);
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesFiltered", store.getCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_PAList_CliniciansControlCheck:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("IsAllEmployeesChecked", checked);
                var list = store.getCliniciansBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.Helper.FindUpdateICheckableItems(store.PADisplaySettingsModel.get("EmployeesGlobalCache"), listOfClinicianIds, checked);
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesGlobalCache", c);
                var checkedList = store.getCliniciansBasedOnSitesAndQuery();
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("EmployeesGlobalCache", checkedList);
                break;

            case CheckboxTarget.Myle_PAList_OnlySelectedSites:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("IsShowOnlySelectedSitesChecked", checked);
                break;

            case CheckboxTarget.Myle_PAList_AppointmentStatusControlCheck:
                let statuses = store.Helper.ChangeCheckStateFromAllICheckable(store.PADisplaySettingsModel.get("AppointmentStatuses"), checked);
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("AppointmentStatuses", statuses);
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("IsAllStatusChecked", checked);
                break;

            case CheckboxTarget.Myle_PAList_AppointmentStatusCheck:
                store.PADisplaySettingsModel = store.PADisplaySettingsModel.set("AppointmentStatuses", store.Helper.FindUpdateICheckableItem<CheckboxUIModel>(store.PADisplaySettingsModel.get("AppointmentStatuses"), id, checked));
                break;

            default:
                break;
        }
    }

    private OnCancelClick(request: LocalEvent, store: PAListStore): void {
        store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", false);
    }

    private OnOkClick(request: LocalEvent, store: PAListStore): void {
        if (store.PAListModel.get("SaveSettingsChecked")) {
            store.PAListActions.SaveDisplaySettings(store.PADisplaySettingsModel);
            store.PAListModel = store.PAListModel.set("SaveSettingsChecked", false);
        }

        store.PAListModel = store.PAListModel.set("ShowPAListDisplaySettings", false);
        store.PAListActions.LoadPAList();
    }
}

export { PAListSettingsContainerHandler };
